/**
 * UnifiedJobDetail - Unified component for viewing/editing job details
 * 
 * Used by both:
 * - Admin/Scheduler (full page view via JobDetailView wrapper)
 * - Technician/Contractor (modal view via JobTicketModal wrapper)
 * 
 * Features:
 * - Customer info display
 * - Job status management with auto-notifications
 * - Equipment Information with Placard Scanner
 * - Notes and Photos
 * - Draft Invoice sync
 * - AI Summary generation
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { TradeFieldsSection } from '@/components/TradeFieldsSection';
import { 
  Trash2, Upload, Camera, Clock, Package, Edit, Plus, X, 
  FileText, Download, DollarSign, Sparkles, Loader2, ChevronDown, ChevronUp, 
  Scan, MapPin, Phone, Mail, Calendar, Wrench, MessageSquare, Bell
} from 'lucide-react';
import { toast } from 'sonner';
import AITextEnhancer from './AITextEnhancer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to convert 24-hour time to 12-hour format
const formatTimeTo12Hour = (time24) => {
  if (!time24) return '';
  
  // Handle time slot format like "10:00-14:00"
  if (time24.includes('-')) {
    const [start, end] = time24.split('-');
    return `${formatSingleTime(start)} - ${formatSingleTime(end)}`;
  }
  
  return formatSingleTime(time24);
};

const formatSingleTime = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${hour12}:${minutes?.toString().padStart(2, '0') || '00'} ${period}`;
};

// Industry-specific label mapping for claim/case/reference field
const REFERENCE_FIELD_LABELS = {
  'HVAC': 'Claim #',
  'Appliance Repair': 'Claim #',
  'Plumbing': 'Work Order #',
  'Electrical': 'Work Order #',
  'Medical Courier': 'Case #',
  'Medical Equipment': 'Case #',
  'Pharmacy': 'Rx #',
  'default': 'Reference #'
};

const getReferenceLabelForIndustry = (industry) => {
  if (!industry) return REFERENCE_FIELD_LABELS.default;
  for (const [key, label] of Object.entries(REFERENCE_FIELD_LABELS)) {
    if (industry.toLowerCase().includes(key.toLowerCase())) {
      return label;
    }
  }
  return REFERENCE_FIELD_LABELS.default;
};

const STATUS_OPTIONS = [
  { value: 'SCH', label: 'Scheduled', color: 'bg-gray-100 text-gray-800' },
  { value: 'IR', label: 'In Route', color: 'bg-slate-200 text-blue-800', triggerNotification: 'en_route' },
  { value: 'IP', label: 'In Progress', color: 'bg-yellow-100 text-yellow-800', triggerNotification: 'arrived' },
  { value: 'COMPLETED', label: 'Completed', color: 'bg-green-100 text-green-800', triggerNotification: 'completed' },
  { value: 'WOP', label: 'Waiting on Parts', color: 'bg-orange-100 text-orange-800' },
  { value: 'BO', label: 'Back Ordered', color: 'bg-red-100 text-red-800' },
  { value: 'WFA', label: 'Waiting for Authorization', color: 'bg-slate-200 text-purple-800' },
  { value: 'CD', label: 'Customer Deciding', color: 'bg-pink-100 text-pink-800' },
  { value: 'RW', label: 'Rework', color: 'bg-indigo-100 text-indigo-800' }
];

// Notification templates - shown to user before sending
const NOTIFICATION_TEMPLATES = {
  en_route: {
    title: 'On My Way Notification',
    message: (workerName, eta) => `🚗 ${workerName} is on the way!\n\nEstimated arrival: ${eta} minutes\n\n📍 Track live location: [tracking link]\n\nWe'll notify you when they arrive.`,
    icon: '🚗'
  },
  arrived: {
    title: 'Arrival Notification', 
    message: (workerName) => `✅ ${workerName} has arrived!\n\nThey should be at your door shortly.`,
    icon: '✅'
  },
  completed: {
    title: 'Job Complete Notification',
    message: (companyName, jobDetails) => `Your service with ${companyName} is complete!\n\nWork performed: ${jobDetails}\n\nThank you for choosing us!`,
    icon: '🎉'
  }
};

const UnifiedJobDetail = ({ 
  appointmentId, 
  currentUser, 
  viewMode = 'modal', // 'modal' or 'page'
  onClose,
  appointment: initialAppointment,
  customer: initialCustomer
}) => {
  const [ticket, setTicket] = useState(null);
  const [appointment, setAppointment] = useState(initialAppointment);
  const [customer, setCustomer] = useState(initialCustomer);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [rewritingNote, setRewritingNote] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoCaption, setPhotoCaption] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [documentFile, setDocumentFile] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');
  const [selectedPhotoId, setSelectedPhotoId] = useState(null);
  const [replacingPhotoId, setReplacingPhotoId] = useState(null);
  const [replacePhotoFile, setReplacePhotoFile] = useState(null);
  const [draftInvoice, setDraftInvoice] = useState(null);
  const [syncingInvoice, setSyncingInvoice] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [aiSummary, setAiSummary] = useState('');
  const [showQuickNote, setShowQuickNote] = useState(false);
  
  // Section expand/collapse
  const [expandedSections, setExpandedSections] = useState({
    customer: true,
    equipment: true,
    jobDetails: true,
    notes: true,
    photos: true,
    documents: true,
    invoice: true
  });
  
  // Notification state
  const [showNotificationPreview, setShowNotificationPreview] = useState(false);
  const [pendingNotification, setPendingNotification] = useState(null);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [etaMinutes, setEtaMinutes] = useState(15);
  
  // Placard scanner state
  const [scanningPlacard, setScanningPlacard] = useState(false);
  const placardInputRef = useRef(null);

  useEffect(() => {
    if (appointmentId) {
      loadData();
    }
  }, [appointmentId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load appointment if not provided via props
      let apptData = appointment;
      let custData = customer;
      
      if (!apptData) {
        const apptResponse = await axios.get(`${API}/appointments/${appointmentId}`);
        apptData = apptResponse.data;
        setAppointment(apptData);
        
        // Load customer
        if (apptData.customer_id && !custData) {
          const custResponse = await axios.get(`${API}/customers/${apptData.customer_id}`);
          custData = custResponse.data;
          setCustomer(custData);
        }
      }
      
      // Load ticket (may not exist, that's OK)
      try {
        const ticketResponse = await axios.get(`${API}/tickets/appointment/${appointmentId}`);
        const t = ticketResponse.data;
        // Hydrate the rich Equipment inputs (which bind to placard_data) from
        // the canonical appliance fields when placard_data is missing OR blank
        // (e.g. equipment set on create / office edit / another surface). CR-12.
        const pd = t && t.placard_data;
        const pdBlank = !pd || (!pd.make && !pd.model && !pd.serial);
        if (t && pdBlank && (t.appliance_brand || t.model_number || t.serial_number)) {
          t.placard_data = {
            ...(pd || {}),
            make: t.appliance_brand || (pd && pd.make) || '',
            model: t.model_number || (pd && pd.model) || '',
            serial: t.serial_number || (pd && pd.serial) || '',
            year: (pd && pd.year) || ''
          };
        }
        setTicket(t);
        
        // Load draft invoice
        try {
          const invoiceResponse = await axios.get(`${API}/tickets/${ticketResponse.data.id}/draft-invoice`);
          if (invoiceResponse.data.exists) {
            setDraftInvoice(invoiceResponse.data.invoice);
          }
        } catch {
          // No draft invoice yet
        }
      } catch {
        // Ticket may not exist yet, that's normal
      }
    } catch (error) {
      console.error('Error loading data:', error);
      // Only show error if we couldn't load the base appointment data
      if (!appointment) {
        toast.error('Failed to load job details');
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto-sync draft invoice when ticket changes
  const syncDraftInvoice = async (ticketId) => {
    if (!ticketId) return;
    
    try {
      setSyncingInvoice(true);
      const response = await axios.post(`${API}/tickets/${ticketId}/auto-draft-invoice`);
      if (response.data.success) {
        const invoiceResponse = await axios.get(`${API}/tickets/${ticketId}/draft-invoice`);
        if (invoiceResponse.data.exists) {
          setDraftInvoice(invoiceResponse.data.invoice);
        }
      }
    } catch (error) {
      console.error('Error syncing draft invoice:', error);
    } finally {
      setSyncingInvoice(false);
    }
  };

  const updateTicket = async (updates) => {
    if (!ticket?.id) return;
    
    try {
      await axios.put(`${API}/tickets/${ticket.id}`, updates);
      toast.success('Updated');
      await loadData();
      
      // Auto-sync draft invoice if relevant fields changed
      if (updates.hours_worked !== undefined || updates.service_call_fee !== undefined || 
          updates.placard_data !== undefined || updates.claim_number !== undefined ||
          updates.description !== undefined || updates.diagnosis !== undefined || 
          updates.parts_notes !== undefined || updates.trade_fields !== undefined) {
        await syncDraftInvoice(ticket.id);
      }
    } catch (error) {
      console.error('Error updating ticket:', error);
      toast.error('Failed to update');
    }
  };

  // CR-20: completing a job ALWAYS runs the ticket chain (draft invoice + Ari),
  // even when there is no customer phone to notify. No appointment-only bypass.
  const completeTicketChain = async () => {
    try {
      if (ticket?.id) {
        await axios.post(`${API}/tickets/${ticket.id}/complete`);
      }
      await loadData();
      toast.success('Job completed & invoice drafted!');
    } catch (error) {
      console.error('Error completing job:', error);
      toast.error('Failed to complete job');
    }
  };

  // Handle status change with notification trigger
  const handleStatusChange = async (newStatus) => {
    const statusOption = STATUS_OPTIONS.find(s => s.value === newStatus);

    // Completion with no phone: still run the full ticket completion chain.
    if (newStatus === 'COMPLETED' && !customer?.phone) {
      await completeTicketChain();
      return;
    }

    // Check if this status change should trigger a notification
    if (statusOption?.triggerNotification && customer?.phone) {
      setPendingNotification({
        type: statusOption.triggerNotification,
        status: newStatus,
        template: NOTIFICATION_TEMPLATES[statusOption.triggerNotification]
      });
      setShowNotificationPreview(true);
    } else {
      // No notification needed, just update status
      await updateStatusDirectly(newStatus);
    }
  };

  const updateStatusDirectly = async (newStatus) => {
    try {
      // Update ticket status
      if (ticket?.id) {
        await axios.put(`${API}/tickets/${ticket.id}`, { status: newStatus });
      }
      
      // Update appointment status
      await axios.put(`${API}/appointments/${appointmentId}/status`, { status: newStatus });
      
      toast.success(`Status updated to ${STATUS_OPTIONS.find(s => s.value === newStatus)?.label || newStatus}`);
      await loadData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  // Send notification and update status
  const sendNotificationAndUpdateStatus = async () => {
    if (!pendingNotification || !customer?.phone) return;
    
    try {
      setSendingNotification(true);
      
      const { type, status } = pendingNotification;
      
      // Call appropriate notification endpoint
      if (type === 'en_route') {
        await axios.post(`${API}/notifications/worker-en-route`, {
          appointment_id: appointmentId,
          customer_phone: customer.phone,
          worker_name: currentUser?.name || 'Your technician',
          eta_minutes: etaMinutes,
          tracking_link: `${window.location.origin}/track/${appointmentId}`
        });
        toast.success('Customer notified - On My Way!');
      } else if (type === 'arrived') {
        await axios.post(`${API}/notifications/worker-arrived`, {
          appointment_id: appointmentId,
          customer_phone: customer.phone,
          worker_name: currentUser?.name || 'Your technician'
        });
        toast.success('Customer notified - Arrival!');
      } else if (type === 'completed') {
        // Job completion notification is sent via job lifecycle
        await axios.post(`${API}/tickets/${ticket?.id}/complete`);
        toast.success('Job completed & customer notified!');
      }
      
      // Update status after notification
      await updateStatusDirectly(status);
      
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Failed to send notification, but status updated');
      await updateStatusDirectly(pendingNotification.status);
    } finally {
      setSendingNotification(false);
      setShowNotificationPreview(false);
      setPendingNotification(null);
    }
  };

  // Skip notification and just update status
  const skipNotificationAndUpdateStatus = async () => {
    if (pendingNotification) {
      await updateStatusDirectly(pendingNotification.status);
    }
    setShowNotificationPreview(false);
    setPendingNotification(null);
  };

  // Placard Scanner
  const handlePlacardScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setScanningPlacard(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        
        try {
          const response = await axios.post(`${API}/ocr/placard`, {
            image_base64: base64,
            company_id: currentUser?.company_id || ticket?.company_id,
            customer_id: ticket?.customer_id,
            job_id: ticket?.appointment_id || ticket?.id,
          });

          const d = response.data?.data || {};

          const placardData = {
            make: d.make || '',
            model: d.model || '',
            serial: d.serial || '',
            year: d.year || '',
            color: d.color || '',
            capacity: d.capacity || '',
            manufacturer: d.manufacturer || '',
            manufacture_date: d.manufacture_date || '',
            intelligence: d.intelligence || null,
          };

          await updateTicket({ placard_data: placardData });
          if (d.intelligence?.human_summary) {
            toast.success('Equipment intelligence ready');
          } else {
            toast.success('Equipment info extracted!');
          }
        } catch (error) {
          console.error('OCR error:', error);
          toast.error('Failed to scan placard. Try a clearer image.');
        }
      };
      reader.readAsDataURL(file);
    } finally {
      setScanningPlacard(false);
      if (placardInputRef.current) {
        placardInputRef.current.value = '';
      }
    }
  };

  // Notes management
  const addNote = async () => {
    if (!newNote.trim() || !ticket?.id) return;

    try {
      await axios.post(`${API}/tickets/${ticket.id}/notes`, null, {
        params: {
          text: newNote,
          user_id: currentUser?.id,
          user_name: currentUser?.name
        }
      });
      toast.success('Note added');
      setNewNote('');
      await loadData();
      await syncDraftInvoice(ticket.id);
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note');
    }
  };

  const rewriteNoteWithAri = async () => {
    if (!newNote.trim() || rewritingNote) return;
    setRewritingNote(true);
    try {
      const ctxParts = [];
      if (ticket?.appliance_brand) ctxParts.push(ticket.appliance_brand);
      if (ticket?.model_number) ctxParts.push(ticket.model_number);
      const context = ctxParts.length ? `Service ticket for ${ctxParts.join(' ')}` : null;
      const { data } = await axios.post(`${API}/ari/insights/rewrite-note`, { text: newNote, context });
      if (data?.rewritten) {
        setNewNote(data.rewritten);
        toast.success('Ari polished your note');
      }
    } catch (error) {
      console.error('Note rewrite failed:', error);
      toast.error('Ari could not rewrite the note');
    } finally {
      setRewritingNote(false);
    }
  };

  // Ari: draft the diagnosis from the job's notes (or description/parts as a
  // fallback). Surfaces the existing /api/ai/generate-diagnosis endpoint.
  const [generatingDiagnosis, setGeneratingDiagnosis] = useState(false);
  // Canonical Field Intelligence record (GET /api/field-intelligence/{id}) —
  // the single read surface; consumed here as a job-readiness strip.
  const [fiRecord, setFiRecord] = useState(null);
  const generateDiagnosisFromNotes = async () => {
    if (generatingDiagnosis) return;
    const noteTexts = (ticket?.notes || []).map((n) => (typeof n === 'string' ? n : n.text)).filter(Boolean);
    const seed = noteTexts.length ? noteTexts : [ticket?.description, ticket?.parts_notes].filter(Boolean);
    if (!seed.length) {
      toast.error('Add a note or description first so Ari has something to work from');
      return;
    }
    setGeneratingDiagnosis(true);
    try {
      const { data } = await axios.post(`${API}/ai/generate-diagnosis`, {
        notes: seed,
        context: {
          customer_name: ticket?.customer_name || '',
          service_type: ticket?.trade || ticket?.service_category || 'service',
          appliance: [ticket?.placard_data?.make, ticket?.placard_data?.model].filter(Boolean).join(' '),
        },
      });
      if (data?.diagnosis) {
        setTicket((prev) => ({ ...prev, diagnosis: data.diagnosis }));
        await updateTicket({ diagnosis: data.diagnosis });
        toast.success('Ari drafted the diagnosis');
      }
    } catch (error) {
      console.error('Diagnosis generation failed:', error);
      toast.error('Ari could not generate a diagnosis');
    } finally {
      setGeneratingDiagnosis(false);
    }
  };

  // Consume the canonical Field Intelligence record so the endpoint is never an
  // island — refreshes when the equipment / diagnosis / pricing inputs change.
  useEffect(() => {
    if (!ticket?.id) { setFiRecord(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/field-intelligence/${ticket.appointment_id || ticket.id}`);
        if (!cancelled) setFiRecord(data);
      } catch {
        if (!cancelled) setFiRecord(null);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.id, ticket?.diagnosis, ticket?.placard_data]);

  const deleteNote = async (noteId) => {    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API}/tickets/${ticket.id}/notes/${noteId}`, {
        params: { user_id: currentUser?.id }
      });
      toast.success('Note deleted');
      setSelectedNoteId(null);
      await loadData();
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note');
    }
  };

  // Photo management
  const uploadPhoto = async () => {
    if (!photoFile || !ticket?.id) return;

    try {
      setUploadingPhoto(true);
      const formData = new FormData();
      formData.append('file', photoFile);
      formData.append('user_id', currentUser?.id);
      formData.append('user_name', currentUser?.name);
      formData.append('caption', photoCaption);

      await axios.post(`${API}/tickets/${ticket.id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Photo uploaded');
      setPhotoFile(null);
      setPhotoCaption('');
      await loadData();
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const deletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;

    try {
      await axios.delete(`${API}/tickets/${ticket.id}/photos/${photoId}`, {
        params: { user_id: currentUser?.id }
      });
      toast.success('Photo deleted');
      setSelectedPhotoId(null);
      await loadData();
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Failed to delete photo');
    }
  };

  // Document management
  const uploadDocument = async () => {
    if (!documentFile || !ticket?.id) return;
    try {
      setUploadingDoc(true);
      const formData = new FormData();
      formData.append('file', documentFile);
      formData.append('user_id', currentUser?.id);
      formData.append('user_name', currentUser?.name);
      await axios.post(`${API}/tickets/${ticket.id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded');
      setDocumentFile(null);
      await loadData();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error?.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(false);
    }
  };

  const deleteDocument = async (documentId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/tickets/${ticket.id}/documents/${documentId}`, {
        params: { user_id: currentUser?.id }
      });
      toast.success('Document deleted');
      await loadData();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error(error?.response?.data?.detail || 'Failed to delete document');
    }
  };

  // AI Summary
  const generateAISummary = async () => {
    if (!ticket?.id || !ticket?.notes?.length) {
      toast.error('Add some notes first before generating a summary');
      return;
    }

    try {
      setGeneratingSummary(true);
      const response = await axios.post(`${API}/tickets/${ticket.id}/generate-invoice-summary`);
      
      if (response.data.success) {
        setAiSummary(response.data.summary);
        toast.success('AI summary generated!');
        
        const invoiceResponse = await axios.get(`${API}/tickets/${ticket.id}/draft-invoice`);
        if (invoiceResponse.data.exists) {
          setDraftInvoice(invoiceResponse.data.invoice);
        }
      }
    } catch (error) {
      console.error('Error generating AI summary:', error);
      toast.error('Failed to generate AI summary');
    } finally {
      setGeneratingSummary(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const canEdit = currentUser?.role === 'admin' || currentUser?.role === 'scheduler';
  const canEditOwnNotes = currentUser?.role === 'technician' || currentUser?.role === 'contractor';

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Notification Preview Modal */}
      {showNotificationPreview && pendingNotification && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-2xl">
                {pendingNotification.template.icon}
              </div>
              <div>
                <h3 className="font-bold text-lg">{pendingNotification.template.title}</h3>
                <p className="text-sm text-gray-500">Send notification to customer?</p>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-4 mb-4 text-sm whitespace-pre-line">
              {pendingNotification.type === 'en_route' 
                ? pendingNotification.template.message(currentUser?.name || 'Technician', etaMinutes)
                : pendingNotification.type === 'arrived'
                ? pendingNotification.template.message(currentUser?.name || 'Technician')
                : pendingNotification.template.message('Company', ticket?.diagnosis || 'Service completed')
              }
            </div>
            
            {pendingNotification.type === 'en_route' && (
              <div className="mb-4">
                <Label className="text-sm text-gray-600">ETA (minutes)</Label>
                <Input
                  type="number"
                  value={etaMinutes}
                  onChange={(e) => setEtaMinutes(parseInt(e.target.value) || 15)}
                  className="mt-1"
                  min={1}
                  max={120}
                />
              </div>
            )}
            
            <div className="text-xs text-gray-500 mb-4">
              <Bell className="w-3 h-3 inline mr-1" />
              Will be sent to: {customer?.phone || 'No phone'}
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={skipNotificationAndUpdateStatus}
                className="flex-1"
                disabled={sendingNotification}
              >
                Skip Notification
              </Button>
              <Button
                onClick={sendNotificationAndUpdateStatus}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={sendingNotification || !customer?.phone}
              >
                {sendingNotification ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Send & Update
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Information */}
      <Card>
        <CardContent className="pt-4">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection('customer')}
          >
            <h3 className="font-bold text-lg flex items-center gap-2">
              <Phone className="w-5 h-5 text-blue-600" />
              Customer Information
            </h3>
            {expandedSections.customer ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
          
          {expandedSections.customer && (
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-gray-500">Name</Label>
                <p className="font-medium">{customer?.name || ticket?.customer_name || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Phone</Label>
                <a href={`tel:${customer?.phone || ticket?.phone}`} className="font-medium text-blue-600 hover:underline">
                  {customer?.phone || ticket?.phone || 'N/A'}
                </a>
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-gray-500">Address</Label>
                <p className="font-medium">{customer?.address || ticket?.address || appointment?.address || 'N/A'}</p>
              </div>
              {customer?.email && (
                <div className="col-span-2">
                  <Label className="text-xs text-gray-500">Email</Label>
                  <a href={`mailto:${customer.email}`} className="font-medium text-blue-600 hover:underline">
                    {customer.email}
                  </a>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Job Status & Details */}
      <Card>
        <CardContent className="pt-4">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection('jobDetails')}
          >
            <h3 className="font-bold text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-green-600" />
              Job Details
            </h3>
            {expandedSections.jobDetails ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
          
          {expandedSections.jobDetails && ticket && (
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-gray-500">Status</Label>
                <Select
                  value={ticket.status}
                  onValueChange={handleStatusChange}
                >
                  <SelectTrigger className="mt-1" data-testid="job-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value} data-testid={`status-option-${opt.value}`}>
                        <span className="flex items-center gap-2">
                          {opt.triggerNotification && <Bell className="w-3 h-3 text-blue-500" />}
                          {opt.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-400 mt-1">
                  <Bell className="w-3 h-3 inline" /> = auto-notifies customer
                </p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Hours Worked</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={ticket.hours_worked || 0}
                  onChange={(e) => setTicket({...ticket, hours_worked: parseFloat(e.target.value) || 0})}
                  onBlur={() => updateTicket({ hours_worked: ticket.hours_worked })}
                  className="mt-1"
                  data-testid="job-hours-input"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Service Call Fee</Label>
                <Input
                  type="number"
                  step="1"
                  value={ticket.service_call_fee || 0}
                  onChange={(e) => setTicket({...ticket, service_call_fee: parseFloat(e.target.value) || 0})}
                  onBlur={() => updateTicket({ service_call_fee: ticket.service_call_fee })}
                  className="mt-1"
                  data-testid="job-service-call-fee-input"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Scheduled</Label>
                <p className="font-medium mt-1">
                  {appointment?.appointment_date || ticket?.scheduled_date} {formatTimeTo12Hour(appointment?.time_slot || ticket?.scheduled_time)}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Equipment Information */}
      <Card>
        <CardContent className="pt-4">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection('equipment')}
          >
            <h3 className="font-bold text-lg flex items-center gap-2">
              <Wrench className="w-5 h-5 text-purple-600" />
              Equipment Information
            </h3>
            <div className="flex items-center gap-2">
              <input
                ref={placardInputRef}
                type="file"
                accept="image/*"
                onChange={handlePlacardScan}
                className="hidden"
                id="placard-upload-unified"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  placardInputRef.current?.click();
                }}
                disabled={scanningPlacard || !ticket}
                className="bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
              >
                {scanningPlacard ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <Scan className="w-4 h-4 mr-1" />
                    Scan Placard
                  </>
                )}
              </Button>
              {expandedSections.equipment ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          </div>
          
          {expandedSections.equipment && ticket && (
            <div className="mt-4">
              {fiRecord && (
                <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="field-intelligence-strip">
                  <span className="text-xs font-semibold text-gray-600">Field Intelligence:</span>
                  {[
                    ['Equipment', !!(fiRecord.equipment?.make || fiRecord.equipment?.model)],
                    ['Intelligence', !!fiRecord.equipment?.intelligence?.warranty_status],
                    ['Diagnosis', !!fiRecord.diagnosis],
                    ['Pricing', (fiRecord.pricing?.total || 0) > 0],
                  ].map(([label, ok]) => (
                    <span
                      key={label}
                      data-testid={`fi-chip-${label.toLowerCase()}`}
                      className={`px-2 py-0.5 rounded-full text-xs border ${ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-gray-50 border-gray-200 text-gray-400'}`}
                    >
                      {ok ? '✓' : '—'} {label}
                    </span>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500 mb-3">
                Scan the equipment placard/data plate to auto-fill. Year is looked up if not on label.
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-xs text-gray-500">{getReferenceLabelForIndustry(ticket.service_category)}</Label>
                  <Input
                    value={ticket.claim_number || ''}
                    onChange={(e) => setTicket({...ticket, claim_number: e.target.value})}
                    onBlur={() => updateTicket({ claim_number: ticket.claim_number })}
                    placeholder="Enter if applicable"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-xs text-gray-500">Make</Label>
                  <Input
                    value={ticket.placard_data?.make || ''}
                    onChange={(e) => setTicket({
                      ...ticket, 
                      placard_data: {...(ticket.placard_data || {}), make: e.target.value}
                    })}
                    onBlur={() => updateTicket({ placard_data: ticket.placard_data })}
                    placeholder="Brand"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-xs text-gray-500">Year</Label>
                  <Input
                    value={ticket.placard_data?.year || ''}
                    onChange={(e) => setTicket({
                      ...ticket, 
                      placard_data: {...(ticket.placard_data || {}), year: e.target.value}
                    })}
                    onBlur={() => updateTicket({ placard_data: ticket.placard_data })}
                    placeholder="Year"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-xs text-gray-500">Model</Label>
                  <Input
                    value={ticket.placard_data?.model || ''}
                    onChange={(e) => setTicket({
                      ...ticket, 
                      placard_data: {...(ticket.placard_data || {}), model: e.target.value}
                    })}
                    onBlur={() => updateTicket({ placard_data: ticket.placard_data })}
                    placeholder="Model #"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-xs text-gray-500">Serial</Label>
                  <Input
                    value={ticket.placard_data?.serial || ''}
                    onChange={(e) => setTicket({
                      ...ticket, 
                      placard_data: {...(ticket.placard_data || {}), serial: e.target.value}
                    })}
                    onBlur={() => updateTicket({ placard_data: ticket.placard_data })}
                    placeholder="Serial #"
                    className="mt-1"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label className="text-xs text-gray-500">Description</Label>
                  <Textarea
                    value={ticket.description || ''}
                    onChange={(e) => setTicket({...ticket, description: e.target.value})}
                    onBlur={() => updateTicket({ description: ticket.description })}
                    placeholder="Issue description"
                    rows={2}
                    className="mt-1"
                  />
                </div>
                
                <div className="col-span-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-gray-500">Diagnosis</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={generateDiagnosisFromNotes}
                      disabled={generatingDiagnosis}
                      className="h-6 px-2 text-xs text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                      data-testid="generate-diagnosis-btn"
                    >
                      {generatingDiagnosis ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                      Generate from notes
                    </Button>
                  </div>
                  <Textarea
                    value={ticket.diagnosis || ''}
                    onChange={(e) => setTicket({...ticket, diagnosis: e.target.value})}
                    onBlur={() => updateTicket({ diagnosis: ticket.diagnosis })}
                    placeholder="What was found / root cause"
                    rows={2}
                    className="mt-1"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label className="text-xs text-gray-500">Parts Used/Needed</Label>
                  <Textarea
                    value={ticket.parts_notes || ''}
                    onChange={(e) => setTicket({...ticket, parts_notes: e.target.value})}
                    onBlur={() => updateTicket({ parts_notes: ticket.parts_notes })}
                    placeholder="List parts"
                    rows={2}
                    className="mt-1"
                  />
                </div>
              </div>

              {ticket.placard_data?.intelligence && (
                <div
                  className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50/60 p-3"
                  data-testid="equipment-intelligence-panel"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm font-bold text-indigo-900">Ari Equipment Intelligence</span>
                  </div>
                  <p className="text-sm text-indigo-900/90 mb-2" data-testid="equipment-intelligence-summary">
                    {ticket.placard_data.intelligence.human_summary || 'Analysis ready.'}
                  </p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {ticket.placard_data.manufacture_date && (
                      <span className="px-2 py-0.5 rounded-full bg-white border border-indigo-200 text-indigo-800">
                        Built {ticket.placard_data.manufacture_date}
                      </span>
                    )}
                    {typeof ticket.placard_data.intelligence.age_years === 'number' && (
                      <span className="px-2 py-0.5 rounded-full bg-white border border-indigo-200 text-indigo-800">
                        {ticket.placard_data.intelligence.age_years} yrs old
                      </span>
                    )}
                    {ticket.placard_data.intelligence.warranty_status && (
                      <span className="px-2 py-0.5 rounded-full bg-white border border-indigo-200 text-indigo-800">
                        Warranty: {String(ticket.placard_data.intelligence.warranty_status).replace(/_/g, ' ')}
                      </span>
                    )}
                    {ticket.placard_data.intelligence.replacement_recommendation && (
                      <span className="px-2 py-0.5 rounded-full bg-white border border-indigo-200 text-indigo-800">
                        Recommend: {String(ticket.placard_data.intelligence.replacement_recommendation).replace(/_/g, ' ')}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {expandedSections.equipment && !ticket && (
            <p className="text-sm text-gray-500 mt-4 italic">
              Equipment info will be available once a job ticket is created.
            </p>
          )}
        </CardContent>
      </Card>

      {/* CR-14: Trade Details — dynamic fields for this ticket's trade.
          Additive; the Equipment Information region above is untouched.
          The ticket is the source of truth, so edits persist immediately. */}
      {ticket && ticket.trade && (
        <Card data-testid="ticket-trade-details-card">
          <CardContent className="pt-4">
            <h3 className="font-bold text-lg flex items-center gap-2 mb-3">
              <Wrench className="w-5 h-5 text-teal-600" />
              Trade Details
            </h3>
            <TradeFieldsSection
              trade={ticket.trade}
              value={ticket.trade_fields || {}}
              onChange={(next) => setTicket({ ...ticket, trade_fields: next })}
              onCommit={(next) => updateTicket({ trade_fields: next })}
              testIdPrefix="ticket-trade-field"
            />
          </CardContent>
        </Card>
      )}

      {/* Notes Section */}
      {ticket && (
        <Card>
          <CardContent className="pt-4">
            <div 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => toggleSection('notes')}
            >
              <h3 className="font-bold text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-orange-600" />
                Notes ({ticket.notes?.length || 0})
              </h3>
              {expandedSections.notes ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
            
            {expandedSections.notes && (
              <div className="mt-4 space-y-3">
                {/* Add Note */}
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Add a note..."
                      rows={2}
                      className="flex-1"
                      data-testid="job-note-input"
                    />
                    <Button onClick={addNote} disabled={!newNote.trim()} data-testid="job-note-add-btn">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={rewriteNoteWithAri}
                    disabled={!newNote.trim() || rewritingNote}
                    data-testid="job-note-polish-btn"
                    className="text-purple-700 border-purple-200 hover:bg-purple-50"
                  >
                    {rewritingNote
                      ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                    Polish with Ari
                  </Button>
                </div>
                
                {/* Notes List */}
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {ticket.notes?.map((note) => (
                    <div key={note.id} className="bg-gray-50 rounded-xl p-3 text-sm">
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-medium text-gray-900">{note.user_name}</span>
                        <span className="text-xs text-gray-400">
                          {new Date(note.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-gray-700">{note.text}</p>
                      {(canEdit || (canEditOwnNotes && note.user_id === currentUser?.id)) && (
                        <button
                          onClick={() => deleteNote(note.id)}
                          className="text-xs text-red-500 hover:text-red-700 mt-2"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  ))}
                  {(!ticket.notes || ticket.notes.length === 0) && (
                    <p className="text-sm text-gray-400 text-center py-4">No notes yet</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Photos Section */}
      {ticket && (
        <Card>
          <CardContent className="pt-4">
            <div 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => toggleSection('photos')}
            >
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Camera className="w-5 h-5 text-pink-600" />
                Photos ({ticket.photos?.length || 0})
              </h3>
              {expandedSections.photos ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
            
            {expandedSections.photos && (
              <div className="mt-4 space-y-3">
                {/* Upload Photo */}
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setPhotoFile(e.target.files?.[0])}
                    />
                    <Input
                      value={photoCaption}
                      onChange={(e) => setPhotoCaption(e.target.value)}
                      placeholder="Caption (optional)"
                      className="mt-2"
                    />
                  </div>
                  <Button onClick={uploadPhoto} disabled={!photoFile || uploadingPhoto}>
                    {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  </Button>
                </div>
                
                {/* Photos Grid */}
                <div className="grid grid-cols-3 gap-2">
                  {ticket.photos?.map((photo) => (
                    <div key={photo.id} className="relative group">
                      <img
                        src={photo.url}
                        alt={photo.caption || 'Job photo'}
                        className="w-full h-24 object-cover rounded-xl"
                      />
                      <button
                        onClick={() => deletePhoto(photo.id)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
                {(!ticket.photos || ticket.photos.length === 0) && (
                  <p className="text-sm text-gray-400 text-center py-4">No photos yet</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Documents Section */}
      {ticket && (
        <Card>
          <CardContent className="pt-4">
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => toggleSection('documents')}
            >
              <h3 className="font-bold text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Documents ({ticket.documents?.length || 0})
              </h3>
              {expandedSections.documents ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>

            {expandedSections.documents && (
              <div className="mt-4 space-y-3" data-testid="ticket-documents-section">
                {/* Upload Document */}
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Input
                      type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.odt,.ppt,.pptx,image/*"
                      onChange={(e) => setDocumentFile(e.target.files?.[0])}
                      data-testid="document-file-input"
                    />
                  </div>
                  <Button onClick={uploadDocument} disabled={!documentFile || uploadingDoc} data-testid="upload-document-btn">
                    {uploadingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  </Button>
                </div>

                {/* Documents List */}
                <div className="space-y-2">
                  {ticket.documents?.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between gap-2 p-2 border rounded-xl bg-gray-50" data-testid={`document-${doc.id}`}>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${doc.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-blue-700 hover:underline truncate"
                        data-testid={`document-link-${doc.id}`}
                      >
                        <FileText className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{doc.original_name || doc.filename}</span>
                        <Download className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
                      </a>
                      <button
                        onClick={() => deleteDocument(doc.id)}
                        className="text-red-500 hover:text-red-700 flex-shrink-0"
                        data-testid={`delete-document-${doc.id}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                {(!ticket.documents || ticket.documents.length === 0) && (
                  <p className="text-sm text-gray-400 text-center py-4">No documents yet</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Draft Invoice Preview */}
      {ticket && draftInvoice && (
        <Card>
          <CardContent className="pt-4">
            <div 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => toggleSection('invoice')}
            >
              <h3 className="font-bold text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Draft Invoice
                {syncingInvoice && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
              </h3>
              {expandedSections.invoice ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
            
            {expandedSections.invoice && (
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Service Call Fee:</span>
                  <span>${(draftInvoice.service_call_fee || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Labor:</span>
                  <span>${(draftInvoice.labor || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Parts:</span>
                  <span>${(draftInvoice.parts?.reduce((sum, p) => sum + (p.total || 0), 0) || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between border-t pt-2 font-bold">
                  <span>Total:</span>
                  <span>${(draftInvoice.total || 0).toFixed(2)}</span>
                </div>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full mt-3"
                  onClick={generateAISummary}
                  disabled={generatingSummary}
                >
                  {generatingSummary ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate AI Summary
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UnifiedJobDetail;
